// One response: metadata, every answer (files, signatures and locations
// shown properly), and editing for people who can edit the questionnaire.
import { cleanAnswers, formatAnswer, languageName, otherKey, pickedSections, resolve, validateAnswers, type Answers, type AnswerValue, type Definition, type Question } from '@survey/core';
import { Download, MapPin, Pencil } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../../api/client';
import type { ResponseRecord } from '../../api/types';
import { formatDate, formatDuration } from '../../format';
import { useI18n } from '../../i18n';
import { QuestionInput } from '../QuestionInput';
import { Drawer, Modal, useToast } from '../ui';

function SourcePill({ source }: { source: ResponseRecord['source'] }) {
  const { t } = useI18n();
  return <span className={`pill ${source === 'mobile' ? 'blue' : source === 'test' ? 'yellow' : ''}`}>{t(`resp.${source}`)}</span>;
}

function FileLink({ value }: { value: AnswerValue }) {
  const { t } = useI18n();
  const toast = useToast();
  const v = value as { url?: string; name?: string };
  if (!v?.url) return null;
  return (
    <button
      className="btn sm"
      onClick={() =>
        api.respondentFileUrl(v.url!).then(
          (blob) => {
            const a = document.createElement('a');
            a.href = blob;
            a.download = v.name ?? 'file';
            a.click();
            setTimeout(() => URL.revokeObjectURL(blob), 1000);
          },
          (e) => toast(e instanceof ApiError ? e.message : t('common.action_failed'), 'error'),
        )
      }
    >
      <Download /> {v.name}
    </button>
  );
}

function AnswerView({ q, value, answers }: { q: Question; value: AnswerValue | undefined; answers: Answers }) {
  const { t, locale } = useI18n();
  if (value === undefined || value === null) return <span className="muted">{t('resp.skipped')}</span>;
  if (q.type === 'file_upload') return <FileLink value={value} />;
  if (q.type === 'signature' && typeof value === 'string') return <img className="signature-view" src={value} alt={t('resp.signature_alt')} />;
  if (q.type === 'location' && typeof value === 'object' && !Array.isArray(value)) {
    const { lat, lng } = value as { lat: number; lng: number };
    return (
      <a className="row gap-4" href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`} target="_blank" rel="noreferrer">
        <MapPin className="icon-sm" /> {formatAnswer(q, value, answers, locale)}
      </a>
    );
  }
  if (q.type === 'matrix' || q.type === 'constant_sum' || q.type === 'text_form')
    return (
      <div className="stack gap-4">
        {formatAnswer(q, value, answers, locale)
          .split('; ')
          .map((line) => (
            <span key={line}>{line}</span>
          ))}
      </div>
    );
  if (q.type === 'ranking' && Array.isArray(value)) return <span style={{ whiteSpace: 'pre-wrap' }}>{value.map((id, i) => `${i + 1}. ${q.options?.find((o) => o.id === id)?.label ?? id}`).join('\n')}</span>;
  return <span style={{ whiteSpace: 'pre-wrap' }}>{formatAnswer(q, value, answers, locale)}</span>;
}

interface Props {
  surveyId: string;
  response: ResponseRecord;
  definition: Definition;
  canEdit: boolean;
  onClose: () => void;
  onSaved: (r: ResponseRecord) => void;
}

export function ResponseDrawer({ surveyId, response: r, definition, canEdit, onClose, onSaved }: Props) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const answers = r.answers as Answers;
  const resolved = useMemo(() => resolve(definition, answers), [definition, answers]);
  const byId = new Map(resolved.questions.map((q) => [q.id, q]));
  const order = (r.questionOrder.length ? r.questionOrder : resolved.questions.filter((q) => resolved.visible[q.id]).map((q) => q.id)).filter((id) => byId.has(id) || id in answers);
  const ending = r.endingId ? definition.endings?.find((e) => e.id === r.endingId)?.name ?? r.endingId : null;
  return (
    <Drawer title={t('resp.drawer_title')} onClose={onClose}>
      <div className="stack gap-16">
        <div className="grid cols-2 small">
          <Meta label={t('resp.col_submitted')} value={formatDate(r.submittedAt, true)} />
          <Meta label={t('resp.received')} value={formatDate(r.receivedAt, true)} />
          <Meta label={t('resp.col_source')} value={<SourcePill source={r.source} />} />
          <Meta label={t('resp.col_version')} value={`v${r.surveyVersion}`} />
          <Meta label={t('resp.col_respondent')} value={r.respondentEmail ?? t('common.anonymous')} />
          <Meta label={t('resp.active_time')} value={formatDuration(r.durationMs)} />
          {r.language && <Meta label={t('runner.language')} value={languageName(r.language)} />}
          {[...pickedSections(definition, answers)].map(([id, titles]) => (
            <Meta key={id} label={t('resp.randomizer_picks')} value={titles.join(' → ')} />
          ))}
          {ending && <Meta label={t('resp.ending')} value={ending} />}
          {Object.entries(r.embedded ?? {}).map(([k, v]) => (
            <Meta key={k} label={k} value={v} />
          ))}
          {r.editedAt && <Meta label={t('resp.edited')} value={formatDate(r.editedAt, true)} />}
        </div>
        <div className="row gap-8 small muted">
          <span className="grow">
            {t('resp.submission_id')} <span className="code">{r.clientSubmissionId}</span>
          </span>
          {canEdit && (
            <button className="btn sm" onClick={() => setEditing(true)}>
              <Pencil /> {t('resp.edit')}
            </button>
          )}
        </div>
        <div className="divider" style={{ margin: 0 }} />
        {order.map((qid, i) => {
          const q = byId.get(qid);
          const text = q?.loop ? q.title.replaceAll('{{loop}}', q.loop.label) : q?.title;
          const title = q ? `${q.questionCode ? `${q.questionCode} · ` : ''}${text}${q.loop && text === q.title ? ` (${q.loop.label})` : ''}` : qid;
          return (
            <div key={qid} className="stack gap-4">
              <div className="row gap-8 small muted">
                <span>
                  {i + 1}. {title}
                </span>
                <span className="grow" />
                {r.timings[qid] !== undefined && <span>{formatDuration(r.timings[qid]!)}</span>}
              </div>
              {q ? <AnswerView q={q} value={answers[qid]} answers={answers} /> : <span>{String(answers[qid] ?? '')}</span>}
            </div>
          );
        })}
      </div>
      {editing && <EditModal surveyId={surveyId} response={r} definition={definition} onClose={() => setEditing(false)} onSaved={(next) => { setEditing(false); onSaved(next); }} />}
    </Drawer>
  );
}

function EditModal({ surveyId, response, definition, onClose, onSaved }: { surveyId: string; response: ResponseRecord; definition: Definition; onClose: () => void; onSaved: (r: ResponseRecord) => void }) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const [answers, setAnswers] = useState<Answers>(() => structuredClone(response.answers as Answers));
  const [busy, setBusy] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  useEffect(() => setServerErrors({}), [answers]);
  const r = resolve(definition, answers);
  const errors = { ...validateAnswers(definition, answers, locale), ...serverErrors };
  const visible = r.questions.filter((q) => r.visible[q.id] && q.type !== 'descriptive');
  async function save() {
    setBusy(true);
    try {
      const updated = await api.updateResponse(surveyId, response.id, cleanAnswers(definition, answers));
      toast(t('resp.edit_saved'));
      onSaved(updated);
    } catch (e) {
      if (e instanceof ApiError && Object.keys(e.fields).length) setServerErrors(e.fields);
      toast(e instanceof ApiError ? e.message : t('common.action_failed'), 'error');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title={t('resp.edit_title')}
      onClose={onClose}
      wide
      footer={
        <>
          <span className="small muted grow">{t('resp.edit_note')}</span>
          <button className="btn" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn primary" onClick={() => void save()} disabled={busy || Object.keys(errors).length > 0}>
            {t('common.save')}
          </button>
        </>
      }
    >
      <div className="stack gap-16">
        {visible.map((q) => (
          <div key={q.id} className="stack gap-8">
            <strong className="small">
              {q.questionCode ? `${q.questionCode} · ` : ''}
              {q.title}
              {q.loop ? ` (${q.loop.label})` : ''}
            </strong>
            {q.type === 'file_upload' || q.type === 'signature' ? (
              <AnswerView q={q} value={answers[q.id]} answers={answers} />
            ) : (
              <QuestionInput
                question={q}
                value={answers[q.id]}
                seed={response.clientSubmissionId}
                invalid={!!errors[q.id]}
                onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))}
                other={answers[otherKey(q.id)]}
                onOtherChange={(v) => setAnswers((a) => ({ ...a, [otherKey(q.id)]: v }))}
              />
            )}
            {errors[q.id] && <p className="error-text">{errors[q.id]}</p>}
          </div>
        ))}
      </div>
    </Modal>
  );
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="stack gap-4">
      <span className="muted">{label}</span>
      <span>{value}</span>
    </div>
  );
}
